import { memo, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useWeatherStore } from '../../store/weatherStore';
import { FORECAST_LAYER_IDS, type ForecastGridType } from '../../config/weatherLayers';
import { warmModelFrames } from '../../services/modelRasterWarmup';
import { useTranslation } from '../../i18n/LanguageContext';
import { useModelLabels } from '../../i18n/useModelLabels';
import { getReadinessSnapshot, subscribeReadiness, FRAME_READY } from '../../services/rasterReadiness';
import { hasCentralLegend } from './CentralLegend';

/**
 * «هل ظهر للزائر إطارٌ واحد على الأقل؟» — تنقلب مرّة واحدة ثم تُلغي اشتراكها.
 *
 * تُستعمل لتأجيل تجهيز النماذج الأخرى إلى ما بعد ظهور الخريطة. ولا نقرأ خريطة
 * الجاهزية بـuseSyncExternalStore هنا عمداً: مرجعها يتغيّر كلّما جهز إطار (مرّتين
 * في الثانية أثناء التحميل)، فيُعاد رسم هذا المكوّن عشرات المرّات — وهو مكوّن
 * مغلّف بـmemo تحديداً كي لا يُعاد رسمه. الاشتراك اليدوي يعطي **إعادة رسم واحدة**.
 */
function useFirstFrameShown(): boolean {
    const [shown, setShown] = useState(false);
    useEffect(() => {
        if (shown) return;
        const check = () => {
            const snap = getReadinessSnapshot();
            for (let i = 0; i < snap.length; i++) {
                if (snap[i] === FRAME_READY) { setShown(true); return true; }
            }
            return false;
        };
        if (check()) return;
        const unsubscribe = subscribeReadiness(() => { if (check()) unsubscribe(); });
        return unsubscribe;
    }, [shown]);
    return shown;
}

/**
 * مبدّل النموذج الجوي على الصفحة الرئيسية (أسلوب Zoom Earth): زرّان GFS/ECMWF
 * ظاهران دائماً فوق الخريطة — النقر يبدّل مجموعة الـ rasters فوراً.
 * بلا props — memo يمنع إعادة الرسم مع كل تغيّر إطار زمني في NewWeatherMap.
 */
export const ModelSwitch = memo(function ModelSwitch() {
    const {
        availableModels,
        selectedModel,
        setSelectedModel,
        visibleLayers,
        currentTimeIndex,
        particlesEnabled,
    } = useWeatherStore(useShallow((s) => ({
        availableModels: s.availableModels,
        selectedModel: s.selectedModel,
        setSelectedModel: s.setSelectedModel,
        visibleLayers: s.visibleLayers,
        currentTimeIndex: s.currentTimeIndex,
        particlesEnabled: s.particlesEnabled,
    })));
    const { t } = useTranslation();
    const { modelResolution } = useModelLabels();
    const ready = useFirstFrameShown();
    const scalarType = FORECAST_LAYER_IDS.find((type) => type !== 'wind' && visibleLayers[type]) as ForecastGridType | undefined;
    const warm = (modelId: typeof selectedModel, intent: 'hover' | 'idle' = 'hover') => {
        if (modelId === selectedModel) return;   // المعروض بالفعل — لا شيء يُحمّى
        void warmModelFrames(modelId, selectedModel, currentTimeIndex, scalarType ?? null, visibleLayers.wind, particlesEnabled, intent);
    };

    // في الخلفية جهّز النموذجين الآخرين بعد اكتمال أول عرض؛ تبديلهما التالي يصيب كاش
    // الصور المفكوكة بدلاً من انتظار الشبكة لأول مرة.
    //
    // ── ثمنه مقيس، والإبقاء عليه قرار مقصود ─────────────────────────────────────
    // قياس على الموقع الحيّ (23/8/2026): الحمولة الأولى 21.6 ميغابايت، منها **11.8
    // لنموذجَي ICON و ECMWF** اللذين لا يراهما الزائر — 55٪ من وزن الصفحة لتبديلٍ قد
    // لا يحدث. ورافقها 21 مهمّة طويلة بمجموع 3.9 ثانية حجب للخيط الرئيسي، و load عند
    // 7.3 ثانية. أي أن هذا السطر هو أثقل بند منفرد في أداء التحميل الأوّل.
    //
    // جُرِّب تأجيله إلى ما بعد `load` + requestIdleCallback مع إلغائه على الشبكات
    // البطيئة، ثمّ أُعيد كما كان: فوريّة تبديل النماذج أولوية منتج مُعلنة، والتأجيل
    // يجعل أوّل تبديل على شاشات اللمس (لا مرور فيها) ينتظر الشبكة.
    //
    // ── أُعيد فتح الملفّ، وتغيّرت المقايضة بعد قياسٍ جديد (2026-09-02) ────────────
    // القياس أعلاه كان يوم ثلاثة نماذج. اليوم ICON معطَّل فبقي واحد، وقياسُ الموقع
    // الحيّ الآن: 18.91 ميغابايت في أول تحميل، **6.55 منها لـECMWF** (16 طلباً) —
    // 35% من وزن الصفحة لنموذج لا يراه الزائر.
    //
    // والأسوأ من الحجم موضعُه في الصفّ: كان الإحماء يدخل الطابور **العاجل**، أي
    // نفس الصفّ الذي ينتظر فيه الإطار الذي يحدّق فيه الزائر الآن، **وبعد 800 مللي
    // ثانية من الإقلاع بلا انتظار ظهور أي شيء على الشاشة**. فالصورة الأولى كانت
    // تقف خلف بيانات نموذج قد لا يُفتح في الجلسة كلّها.
    //
    // ما تغيّر ثلاثة أشياء، وكلّها تحفظ فوريّة التبديل التي كانت سبب إبقاء السطر:
    //   ١) التجهيز الخامل صار **خلفياً** — بايتات بلا فكّ، لا يزاحم أحداً. والفكّ من
    //      بايتات محفوظة أجزاء من الثانية، فالتبديل يبقى فورياً.
    //   ٢) لا يبدأ قبل أن يرى الزائر خريطته (‏ready)، فلا شيء يسبق الصورة الأولى.
    //   ٣) نافذته ثلاثة إطارات لا خمسة؛ والخمسة تصل عند اللمس/المرور بأولوية عاجلة
    //      (‏onPointerEnter/onPointerDown أدناه) — أي عند نيّة تبديلٍ حقيقية.
    //
    // ‏1500ms لا 800: الأولى ثانيةٌ ونصف يملأ فيها الطابور نافذة الشريط القريبة.
    useEffect(() => {
        if (!ready) return;
        const timer = window.setTimeout(() => {
            for (const model of availableModels) if (model.id !== selectedModel) warm(model.id, 'idle');
        }, 1500);
        return () => window.clearTimeout(timer);
    }, [ready, availableModels, selectedModel, currentTimeIndex, scalarType, visibleLayers.wind, particlesEnabled]);

    return (
        /* الموضع على الجوّال يتبع المفتاح تماماً كالشعار: تحت الشريط عند 40، وفي
           مكانه عند 10 حين لا مفتاح للطبقة الفعّالة (قمر/رادار). نفس الفارق 2px عن
           الشعار محفوظ في الحالتين، فلا يختلّ اصطفافهما البصري. */
        <div className={`model-switch-root absolute bottom-5 left-4 right-auto transform-none z-[700] flex gap-1.5 justify-center p-1 w-fit rounded-full bg-[rgba(20,24,31,0.72)] light:bg-white/90 border border-white/[0.12] light:border-black/[0.08] backdrop-blur-[8px] pointer-events-auto ${hasCentralLegend(visibleLayers) ? 'max-[768px]:top-10' : 'max-[768px]:top-[10px]'} max-[768px]:bottom-auto max-[768px]:left-auto max-[768px]:right-[10px] max-[768px]:gap-[5px] max-[768px]:p-1 max-[768px]:z-[600] legend-hidden:max-[768px]:hidden`}>
            {availableModels.map((model) => (
                <button
                    key={model.id}
                    className={`flex items-baseline gap-[5px] px-[14px] py-[5px] border-none rounded-full text-[13px] cursor-pointer transition-[background,color] duration-150 max-[768px]:px-3 max-[768px]:py-1 max-[768px]:text-[11px] ${selectedModel === model.id
                        ? 'bg-[#6d5efc] text-white light:text-white'
                        : 'bg-transparent text-white/[0.72] light:text-[#444] hover:text-white hover:bg-white/[0.08]'}`}
                    onPointerEnter={() => warm(model.id)}
                    onPointerDown={() => warm(model.id)}
                    onClick={() => setSelectedModel(model.id)}
                    type="button"
                    title={t('model.tooltip', { name: model.name, resolution: modelResolution(model) })}
                >
                    <span>{model.name}</span>
                    <span className="text-[11px] opacity-70 max-[768px]:hidden">{modelResolution(model)}</span>
                </button>
            ))}
        </div>
    );
});
